import React, { useState, useEffect, useMemo, useCallback } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import '../pagination/pagination.css';
import { IoAddSharp } from 'react-icons/io5';
import Pagination from '../pagination/pagination';
import Select from 'react-select';
import WindowedSelect from 'react-windowed-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getUserRole } from '../../../Redux/selecters';
import NoAccess from '../../../Routes/noAccess';
import LeaseApiService from '../../services/apiService';
import { fmt3 } from '../leaseGeneralInvoiceReceipt/allocationHelpers';
import { getSelectedFinancialYear, parseApiDate, selectStyles, toApiDate } from '../leaseGeneralInvoiceReceipt/formUtils';
import LeaseBalanceRefundEdit from './LeaseBalanceRefundEdit';
import * as XLSX from 'xlsx-js-style';
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';

const REPORT_URL = process.env.REACT_APP_REPORTS_URL;
export const BALANCE_REFUND_BASE = '/Lease/Management/Lease/BalancePayment';
const FORM_NAME = 'Lease Balance Amount Refund';
const PAGE_SIZE = 50;
const STATUSES = ['All', 'Pending', 'Approved'];

const apiService = new LeaseApiService();

// Read synchronously so "no access" is not flashed before an effect runs.
const readPermission = (formName) => {
    try {
        const stored = JSON.parse(localStorage.getItem('RoleAccess')) || [];
        return stored.find((acc) => acc.formName === formName);
    } catch (error) {
        return undefined;
    }
};

const defaultFilters = () => ({
    voucherNo: '',
    leaseNo: '',
    tenantNo: '',
    propertyNo: '',
    unitNo: '',
    paymentMethod: '',
    status: 'Pending',
    fromDate: moment().startOf('month').format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD'),
});

// The query string is the source of truth, so Back and refresh restore the same view.
const filtersFromParams = (params) => {
    const d = defaultFilters();
    const out = {};
    Object.keys(d).forEach((key) => {
        out[key] = params.has(key) ? params.get(key) : d[key];
    });
    if (!STATUSES.includes(out.status)) out.status = d.status;
    return out;
};

const StatusBadge = ({ approved }) => (
    <span className={`badge ${approved ? 'bg-success' : 'bg-warning text-dark'}`}>{approved ? 'Approved' : 'Pending'}</span>
);

const StatusCell = ({ data }) => (data && data.voucherNo && !data.isTotal ? <StatusBadge approved={data.isApproved} /> : null);

const ActionDotsCell = ({ data, context }) => {
    if (!data) return null;
    if (data.isTotal) return <span className="fw-bold">{data.voucherNo}</span>;
    const handleClick = (e) => {
        e.stopPropagation();
        context?.setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, data });
    };
    return (
        <div className="center pt-1">
            <span onClick={handleClick} style={{ cursor: 'pointer', fontSize: '24px' }}>
                &#8942;
            </span>
        </div>
    );
};

const LeaseBalanceRefundList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [permissions] = useState(() => readPermission(FORM_NAME));
    const userRole = useSelector(getUserRole);
    const isAdminOrFinance = userRole === "Admin" || userRole === "FinanceAdmin" || userRole === 'Finance Admin' || userRole === " Finance Admin" || userRole === "Finance Admin ";
    const [FilterTab, setFilterTab] = useState(true);
    const [loading, setLoading] = useState(true);
    const [rowData, setRowData] = useState([]);
    const [pagination, setPagination] = useState();
    const [reloadKey, setReloadKey] = useState(0);
    const [highlight, setHighlight] = useState(location.state?.highlightVoucherNo || '');
    const [dialog, setDialog] = useState(null); // { voucherNo, financialYear, mode }
    const [contextMenu, setContextMenu] = useState(null);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [exporting, setExporting] = useState(false);
    const { currencyPrecision } = useCurrency();

    useEffect(() => {
        const handleClickOutside = () => setContextMenu(null);
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    const [leaseList, setLeaseList] = useState([]);
    const [methodList, setMethodList] = useState([]);
    const [tenantList, setTenantList] = useState([]);
    const [propertyList, setPropertyList] = useState([]);
    const [unitList, setUnitList] = useState([]);

    // Fixed to the app's selected financial year, like the General Invoice list.
    const [financialYear] = useState(getSelectedFinancialYear());
    const applied = useMemo(() => filtersFromParams(searchParams), [searchParams]);
    const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1);
    const [draft, setDraft] = useState(applied);

    // Keep the form in step with the URL (Back / Forward / Reset).
    useEffect(() => {
        setDraft(applied);
    }, [applied]);

    useEffect(() => {
        (async () => {
            try {
                const params = new URLSearchParams();
                params.append('dropdownTypes', 'LBRLeaseNumbers,LBRPaymentTypes');
                const res = await apiService.getApiParamater('MULTI_BLNC_REF', `?${params.toString()}`);
                setLeaseList(res?.data?.dropdowns?.LBRLeaseNumbers || []);
                setMethodList(res?.data?.dropdowns?.LBRPaymentTypes || []);
            } catch (error) {
                console.error('Error fetching dropdown data:', error);
            }
            try {
                // Same property dropdown the Lease Receipt list uses.
                const params = new URLSearchParams();
                params.append('dropdownTypes', 'ReceiptPropertyNos');
                const res = await apiService.getApiParamater('RECEIPT_DROPDOWN', `?${params.toString()}`);
                setPropertyList(res?.data?.dropdowns?.PropertyNo || []);
            } catch (error) {
                console.error('Error fetching properties:', error);
            }
            try {
                // Same tenant dropdown the General Invoice list uses.
                const res = await apiService.post('GEN_INV_DROPDOWNS', { dropdownTypes: ['Tenants'] });
                setTenantList(res?.data?.dropdowns?.Tenants || []);
            } catch (error) {
                console.error('Error fetching tenants:', error);
            }
        })();
    }, []);

    // Units depend on the chosen property (same cascade the Lease Receipt list uses).
    useEffect(() => {
        if (!draft.propertyNo) {
            setUnitList([]);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const params = new URLSearchParams();
                params.append('DropdownFor', 'LRSearchUnitNosByPropertyId');
                params.append('CascadValue', draft.propertyNo);
                const res = await apiService.getApiParamater('CASCAD_DROP', `?${params.toString()}`);
                if (!cancelled) setUnitList(res?.data?.items || []);
            } catch (error) {
                console.error('Error fetching units:', error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [draft.propertyNo]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await apiService.getBalanceRefundList({
                voucherNo: applied.voucherNo,
                leaseNo: applied.leaseNo,
                tenantNo: applied.tenantNo,
                propertyNo: applied.propertyNo,
                unitNo: applied.unitNo,
                paymentMethod: applied.paymentMethod,
                status: applied.status === 'All' ? '' : applied.status,
                fromDate: applied.fromDate,
                toDate: applied.toDate,
                financialYear,
                pageNo: page,
                pageSize,
            });
            if (cancelled) return;
            setLoading(false);
            if (res.status === 204) {
                setRowData([]);
                setPagination({ currentPage: 1, totalPages: 1, totalRecords: 0 });
            } else if (res.ok) {
                setRowData(res.result || []);
                setPagination(res.pagination);
            } else {
                setRowData([]);
                toast.error(res.message);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [applied, page, pageSize, reloadKey]);

    const writeParams = (filters, pageNo) => {
        const next = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && `${value}`.trim() !== '') next.set(key, `${value}`.trim());
        });
        next.set('page', pageNo);
        setSearchParams(next);
    };

    const handleSearch = () => writeParams(draft, 1);
    const handleReset = () => writeParams(defaultFilters(), 1);
    const handlePage = (pageNo) => writeParams(applied, pageNo);
    const setDraftField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const handleNew = () => navigate(`${BALANCE_REFUND_BASE}/create`);

    const handlePrint = (row) => {
        const token = localStorage.getItem('Token');
        const DB = localStorage.getItem('DataBase');
        if (!token) {
            toast.warning('Auth token is missing! Unable to proceed.');
            return;
        }
        const popupWidth = 1000;
        const popupHeight = 600;
        const popupLeft = (window.screen.width - popupWidth) / 2;
        const popupTop = (window.screen.height - popupHeight) / 2;
        const popup = window.open(
            '',
            'ReportPopup',
            `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},resizable=yes,scrollbars=yes`
        );
        if (!popup) return;
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = REPORT_URL;
        form.target = 'ReportPopup';
        [
            { name: 'authToken', value: token },
            { name: '_db', value: DB },
            { name: 'ReportName', value: 'PAYMENTVOUCHER' },
            { name: 'voucherNo', value: row.voucherNo },
            { name: 'FYear', value: row.financialYear || getSelectedFinancialYear() },
        ].forEach(({ name, value }) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const openDialog = useCallback((row, mode) => {
        setDialog({ voucherNo: row.voucherNo, financialYear: row.financialYear, mode });
    }, []);

    const handleDialogClose = (saved) => {
        if (saved && dialog) {
            setHighlight(dialog.voucherNo);
            setReloadKey((k) => k + 1);
        }
        setDialog(null);
    };

    const dateFormatter = (params) => {
        const d = parseApiDate(params.value);
        return d ? moment(d).format('DD-MMM-YYYY') : '';
    };

    // Fetches every page for the applied filters, so the export is not limited to the visible page.
    const fetchAllRows = async () => {
        const base = {
            voucherNo: applied.voucherNo,
            leaseNo: applied.leaseNo,
            tenantNo: applied.tenantNo,
            propertyNo: applied.propertyNo,
            unitNo: applied.unitNo,
            paymentMethod: applied.paymentMethod,
            status: applied.status === 'All' ? '' : applied.status,
            fromDate: applied.fromDate,
            toDate: applied.toDate,
            financialYear,
            pageSize: 500,
        };
        const all = [];
        let pageNo = 1;
        let totalPages = 1;
        do {
            const res = await apiService.getBalanceRefundList({ ...base, pageNo });
            if (res.status === 204) break;
            if (!res.ok) throw new Error(res.message);
            all.push(...(res.result || []));
            totalPages = res.pagination?.totalPages || 1;
            pageNo += 1;
        } while (pageNo <= totalPages);
        return all;
    };

    const exportExcel = async () => {
        if (exporting || !rowData.length) return;
        setExporting(true);
        try {
            const data = await fetchAllRows();
            if (!data.length) {
                toast.info('No data to export.');
                return;
            }
            const decimalNumbers = currencyPrecision?.decimalNumbers ?? 3;
            const amountFmt = `#,##0.${'0'.repeat(decimalNumbers)}`;
            const fmtDate = (v) => {
                const d = parseApiDate(v);
                return d ? moment(d).format('DD-MMM-YYYY') : '';
            };
            const columns = [
                { header: 'Status', value: (r) => (r.isApproved ? 'Approved' : 'Pending') },
                { header: 'Voucher No', value: (r) => r.voucherNo ?? '' },
                { header: 'Date', value: (r) => fmtDate(r.postingDate), align: 'center' },
                { header: 'Lease No', value: (r) => r.leaseNo ?? '' },
                { header: 'Property', value: (r) => r.propertyName ?? '' },
                { header: 'Unit', value: (r) => r.unitNo ?? '' },
                { header: 'Tenant', value: (r) => r.tenantName ?? '' },
                { header: 'Paid To', value: (r) => r.paidTo ?? '' },
                { header: 'Payment Method', value: (r) => r.paymentMethod ?? '' },
                { header: 'Cheque No', value: (r) => r.chequeNo ?? '' },
                { header: 'Bank / Cash Account', value: (r) => r.bankAccountName || r.bankAccountCode || '' },
                { header: 'Amount', value: (r) => Number(r.amount || 0), type: 'number', align: 'right' },
            ];
            const amountIndex = columns.findIndex((c) => c.header === 'Amount');
            const dataRows = data.map((r) => columns.map((c) => c.value(r)));
            const totalsRow = columns.map((c, i) => {
                if (i === 0) return 'Total';
                if (i === amountIndex) return data.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
                return '';
            });
            const totalRecordsRow = columns.map((c, i) => (i === 0 ? 'Total Records' : i === 1 ? data.length : ''));

            const worksheet = XLSX.utils.aoa_to_sheet([
                ['LEASE BALANCE REFUND LIST'],
                [`From ${fmtDate(applied.fromDate)} To ${fmtDate(applied.toDate)}`],
                columns.map((c) => c.header),
                ...dataRows,
                totalsRow,
                totalRecordsRow,
            ]);
            const HEADER_ROW = 2;
            const FIRST_DATA_ROW = 3;
            const totalsRowIndex = FIRST_DATA_ROW + dataRows.length;

            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: columns.length - 1 } },
            ];
            worksheet['!cols'] = columns.map((c, i) => {
                const maxLength = Math.max(c.header.length, ...dataRows.map((row) => String(row[i] ?? '').length));
                return { wch: Math.min(Math.max(maxLength + 3, 12), 35) };
            });
            worksheet['!rows'] = [{ hpt: 28 }, { hpt: 20 }, { hpt: 30 }];
            worksheet['!freeze'] = { ySplit: HEADER_ROW + 1 };

            const edge = { style: 'thin', color: { rgb: 'FFD9D9D9' } };
            const border = { top: edge, bottom: edge, left: edge, right: edge };
            const styleCell = (r, c, style) => {
                const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
                if (cell) cell.s = { ...style, border };
                return cell;
            };

            styleCell(0, 0, {
                font: { bold: true, sz: 16, color: { rgb: 'FFFFFFFF' } },
                fill: { fgColor: { rgb: 'FF6E5199' } },
                alignment: { horizontal: 'left', vertical: 'center' },
            });
            styleCell(1, 0, {
                font: { sz: 10, color: { rgb: 'FF000000' } },
                alignment: { horizontal: 'left', vertical: 'center' },
            });
            columns.forEach((c, ci) => {
                styleCell(HEADER_ROW, ci, {
                    font: { bold: true, sz: 10, color: { rgb: 'FFFFFFFF' } },
                    fill: { fgColor: { rgb: 'FF666666' } },
                    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                });
            });
            const applyNumberFormat = (cell, col) => {
                if (cell && col.type === 'number') {
                    cell.t = 'n';
                    cell.z = amountFmt;
                }
            };
            dataRows.forEach((row, ri) => {
                columns.forEach((col, ci) => {
                    const cell = styleCell(FIRST_DATA_ROW + ri, ci, {
                        font: { sz: 10, color: { rgb: 'FF000000' } },
                        alignment: { vertical: 'center', horizontal: col.align || 'left' },
                    });
                    applyNumberFormat(cell, col);
                });
            });
            columns.forEach((col, ci) => {
                const cell = styleCell(totalsRowIndex, ci, {
                    font: { bold: true, sz: 10, color: { rgb: 'FF000000' } },
                    fill: { fgColor: { rgb: 'FFF0EDF5' } },
                    alignment: { vertical: 'center', horizontal: col.align || 'left' },
                });
                applyNumberFormat(cell, col);
                const countCell = styleCell(totalsRowIndex + 1, ci, {
                    font: { bold: true, sz: 10, color: { rgb: 'FF000000' } },
                    alignment: { vertical: 'center', horizontal: 'left' },
                });
                if (countCell && ci === 1) countCell.t = 'n';
            });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Refund');
            XLSX.writeFile(workbook, 'Lease_Balance_Refund_List.xlsx');
        } catch (error) {
            console.error('Error exporting refund list:', error);
            toast.error(error?.message || 'Unable to export to Excel.');
        } finally {
            setExporting(false);
        }
    };

    const columnDefs = useMemo(() => [
        {
            headerName: '', width: 40, pinned: 'left', suppressMovable: true, sortable: false,
            cellRenderer: ActionDotsCell,
            colSpan: (params) => (params.data?.isTotal ? 3 : 1),
        },
        { headerName: 'Status', field: 'isApproved', width: 110, pinned: 'left', suppressMovable: true, cellRenderer: StatusCell },
        { headerName: 'Voucher No', field: 'voucherNo', filter: true, floatingFilter: true, width: 140, pinned: 'left', suppressMovable: true },
        { headerName: 'Date', field: 'postingDate', filter: true, floatingFilter: true, valueFormatter: dateFormatter, width: 120, suppressMovable: true },
        { headerName: 'Lease No', field: 'leaseNo', filter: true, floatingFilter: true, width: 120, suppressMovable: true },
        { headerName: 'Property', field: 'propertyName', filter: true, floatingFilter: true, width: 180, suppressMovable: true },
        { headerName: 'Unit', field: 'unitNo', filter: true, floatingFilter: true, width: 100, suppressMovable: true },
        { headerName: 'Tenant', field: 'tenantName', filter: true, floatingFilter: true, width: 200, suppressMovable: true },
        { headerName: 'Paid To', field: 'paidTo', filter: true, floatingFilter: true, width: 180, suppressMovable: true },
        { headerName: 'Payment Method', field: 'paymentMethod', filter: true, floatingFilter: true, width: 140, suppressMovable: true },
        { headerName: 'Cheque No', field: 'chequeNo', filter: true, floatingFilter: true, width: 120, suppressMovable: true },
        {
            headerName: 'Bank / Cash Account', field: 'bankAccountName', filter: true, floatingFilter: true, width: 200, suppressMovable: true,
            valueGetter: (p) => p.data?.bankAccountName || p.data?.bankAccountCode,
        },
        {
            headerName: 'Amount', field: 'amount', filter: true, floatingFilter: true, width: 130, suppressMovable: true, type: 'rightAligned',
            valueFormatter: (p) => (p.value === null || p.value === undefined ? '' : fmt3(p.value)),
        },
    ], []);

    const pageTotal = useMemo(() => rowData.reduce((sum, r) => sum + (Number(r.amount) || 0), 0), [rowData]);
    const pinnedBottomRowData = useMemo(
        () => (rowData.length ? [{ voucherNo: 'Page Total', amount: pageTotal, isTotal: true }] : []),
        [rowData, pageTotal],
    );

    const methodOptions = methodList.map((m) => ({ value: m.text, label: m.text }));
    const tenantOptions = tenantList.map((t) => ({ value: t.value, label: `${t.value} - ${t.text}` }));
    const propertyOptions = propertyList.map((p) => ({ value: p.value, label: p.text }));
    const unitOptions = unitList
        .slice()
        .sort((x, y) => x.text.localeCompare(y.text, undefined, { numeric: true, sensitivity: 'base' }))
        .map((u) => ({ value: u.text, label: u.text }));
    const leaseOptions = leaseList.map((l) => ({ value: l.text, label: l.text }));

    return (
        <div className="p-3">
            {permissions?.canView === true ? (
                <>
                    <div className="cost-center-section">
                        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
                            <div className="param-label pt-1">Search Parameters</div>
                        </div>
                        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px' }}>
                                    <div>
                                        <label className="form-label">Voucher No</label>
                                        <input
                                            type="text"
                                            className="p-2 form-control"
                                            placeholder="Voucher No"
                                            value={draft.voucherNo}
                                            onChange={(e) => setDraftField('voucherNo', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Lease No</label>
                                        <WindowedSelect
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            styles={selectStyles}
                                            isClearable
                                            value={draft.leaseNo ? { value: draft.leaseNo, label: draft.leaseNo } : null}
                                            onChange={(opt) => setDraftField('leaseNo', opt?.value || '')}
                                            options={leaseOptions}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Tenant</label>
                                        <WindowedSelect
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            styles={selectStyles}
                                            isClearable
                                            value={draft.tenantNo ? tenantOptions.find((o) => o.value === draft.tenantNo) || { value: draft.tenantNo, label: draft.tenantNo } : null}
                                            onChange={(opt) => setDraftField('tenantNo', opt?.value || '')}
                                            options={tenantOptions}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Property</label>
                                        <WindowedSelect
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            styles={selectStyles}
                                            isClearable
                                            value={draft.propertyNo ? propertyOptions.find((o) => o.value === draft.propertyNo) || { value: draft.propertyNo, label: draft.propertyNo } : null}
                                            onChange={(opt) => setDraft((prev) => ({ ...prev, propertyNo: opt?.value || '', unitNo: '' }))}
                                            options={propertyOptions}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Unit No</label>
                                        <WindowedSelect
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            styles={selectStyles}
                                            isClearable
                                            isDisabled={!draft.propertyNo}
                                            value={draft.unitNo ? { value: draft.unitNo, label: draft.unitNo } : null}
                                            onChange={(opt) => setDraftField('unitNo', opt?.value || '')}
                                            options={unitOptions}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Payment Method</label>
                                        <Select
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            styles={selectStyles}
                                            isClearable
                                            value={draft.paymentMethod ? { value: draft.paymentMethod, label: draft.paymentMethod } : null}
                                            onChange={(opt) => setDraftField('paymentMethod', opt?.value || '')}
                                            options={methodOptions}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Status</label>
                                        <div className="btn-group w-100" role="group">
                                            {STATUSES.map((s) => (
                                                <button
                                                    key={s}
                                                    type="button"
                                                    className="btn"
                                                    style={draft.status === s
                                                        ? { background: '#6E5199', color: '#fff', border: '1px solid #6E5199' }
                                                        : { background: '#fff', color: '#6E5199', border: '1px solid #6E5199' }}
                                                    onClick={() => setDraftField('status', s)}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="form-label">From Date</label>
                                        <DatePicker
                                            selected={parseApiDate(draft.fromDate)}
                                            onChange={(d) => setDraftField('fromDate', toApiDate(d) || '')}
                                            dateFormat="dd-MMM-yyyy"
                                            className="p-2 form-control w-100"
                                            isClearable
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">To Date</label>
                                        <DatePicker
                                            selected={parseApiDate(draft.toDate)}
                                            onChange={(d) => setDraftField('toDate', toApiDate(d) || '')}
                                            dateFormat="dd-MMM-yyyy"
                                            className="p-2 form-control w-100"
                                            isClearable
                                        />
                                    </div>
                                    <div className="d-flex gap-2 align-items-end">
                                        <div onClick={handleSearch} className="mainbtnGry flex-end gap-2 w-50">
                                            <FaFileCircleExclamation color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Search</div>
                                        </div>
                                        <div onClick={handleReset} className="mainbtnRed flex-end gap-2 w-50">
                                            <RiRefreshLine color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Reset</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border-box2 p-3 mt-2">
                        <div className="d-flex justify-contant-between">
                            <h5 className="col mainInvHead">Lease Balance Refund List</h5>
                            <div className="d-flex justify-content-end gap-1">
                                <button
                                    className="btn btn-secondary mx-2"
                                    type="button"
                                    style={{ cursor: rowData.length > 0 && !exporting ? 'pointer' : 'not-allowed' }}
                                    disabled={!rowData.length || exporting}
                                    onClick={exportExcel}
                                >
                                    {exporting ? 'Exporting...' : 'Export as Excel'}
                                </button>
                                {permissions?.canAdd === true && (
                                    <div className="col center">
                                        <button className="mainbtn" type="button" onClick={handleNew}>
                                            <IoAddSharp color="#fff" className="center mt-1" /> <div>New Refund</div>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {loading ? (
                            <div>Loading...</div>
                        ) : rowData.length === 0 ? (
                            <div className="text-center text-muted p-4">No refund vouchers found for the selected filters.</div>
                        ) : (
                            <div className="gridshad mt-2">
                                <div className="ag-theme-quartz" style={{ height: 650 }}>
                                    <AgGridReact
                                        getRowStyle={(params) => {
                                            if (params.data?.isTotal) return { background: '#E8E4F0', fontWeight: 'bold' };
                                            if (highlight && params.data?.voucherNo === highlight) return { background: '#FFF3C4' };
                                            return params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' };
                                        }}
                                        rowData={rowData}
                                        enableFilter={true}
                                        floatingFilter={true}
                                        columnDefs={columnDefs}
                                        pinnedBottomRowData={pinnedBottomRowData}
                                        context={{ setContextMenu }}
                                    />
                                </div>
                                <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={handlePage} />
                            </div>
                        )}
                    </div>
                    {dialog && (
                        <LeaseBalanceRefundEdit
                            voucherNo={dialog.voucherNo}
                            financialYear={dialog.financialYear}
                            mode={dialog.mode}
                            onClose={handleDialogClose}
                        />
                    )}
                    {contextMenu && (
                        <div
                            style={{
                                position: 'absolute',
                                top: contextMenu.mouseY,
                                left: contextMenu.mouseX,
                                backgroundColor: 'white',
                                border: '1px solid gray',
                                zIndex: 1000,
                                padding: '10px 30px',
                                borderRadius: '8px',
                                boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
                            }}
                        >
                            <div style={{ display: 'grid' }}>
                                <button
                                    className="btn btn-secondary my-1"
                                    onClick={() => {
                                        openDialog(contextMenu.data, 'view');
                                        setContextMenu(null);
                                    }}
                                >
                                    View
                                </button>
                                {contextMenu.data?.canEdit && isAdminOrFinance && (
                                    <button
                                        className="btn btn-secondary mt-1"
                                        onClick={() => {
                                            openDialog(contextMenu.data, 'edit');
                                            setContextMenu(null);
                                        }}
                                    >
                                        Edit
                                    </button>
                                )}
                                <button
                                    className="btn btn-secondary mt-1"
                                    onClick={() => {
                                        handlePrint(contextMenu.data);
                                        setContextMenu(null);
                                    }}
                                >
                                    Print
                                </button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <NoAccess />
            )}
        </div>
    );
};

export default LeaseBalanceRefundList;
